import React, { useCallback, useContext, useEffect, useState } from 'react';
import classnames from 'classnames';
import { add } from 'date-fns';
import styled from 'styled-components';

// Components
import TimeSlotsPanel from '../../_molecules/TimeSlotsPanel';
import LoadingArea from '@molecules/LoadingArea';
import { BodyMedium, ButtonRedesign, DividerLightGreyStyled, H4 } from '@styled';

// Helpers
import { EventDetailProvider } from '../../pages/MyIcsc/Events/EventDetailProvider';
import colors from '@general/colors';
import { formatDateInEastern, getDatesForDateRange } from '@general/dates';

const availabilityStatuses = {
    AVAILABLE: 'available',
    UNAVAILABLE: 'unavailable',
};

const TimeSlotsWrapperOuter = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-left: -19px;
    margin-right: -19px;
`;

const TimeSlotsWrapperInner = styled.div`
    margin-bottom: 32px;
    padding-left: 19px;
    padding-right: 19px;
    width: 460px;

    &.border {
        border-right: 1px solid ${colors.lightGray};
        margin-right: 19px;
        padding-right: 38px;
        width: 479px;
    }
`;

const MyEventAvailability = () => {
    const { event } = useContext(EventDetailProvider);
    const [openHoursByDay, setOpenHoursByDay] = useState(null);

    // -----------------------------
    // Helpers
    // -----------------------------

    const getTimeSlots = useCallback(({ date, startTime, endTime, duration }) => {
        const timeSlots = [];
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        const startHour = startTime / 100;
        const startMinute = startTime % startHour;
        const endHour = endTime / 100;
        const endMinute = endTime % endHour;

        let moving = new Date(year, month, day, startHour, startMinute);
        const end = new Date(year, month, day, endHour, endMinute);

        while (moving < end) {
            const slotStart = moving;
            const slotEnd = add(moving, { minutes: duration });

            timeSlots.push({
                start: slotStart,
                end: slotEnd,
                availability: availabilityStatuses.AVAILABLE,
            });

            moving = slotEnd;
        }

        return timeSlots;
    }, []);

    const toggleAvailability = async timeSlot => {
        //
    };

    // -----------------------------
    // Effects
    // -----------------------------

    // Generate time slots for the event based on event hours + combined unavailability between appointments, calendar events, and resource blocks
    useEffect(() => {
        const eventDays = getDatesForDateRange({
            startDate: event.meeting_start_time,
            endDate: event.meeting_end_time,
        });

        const eventDaysWithHours = eventDays.map(eventDay => ({
            date: eventDay.date,
            startTime: 800,
            endTime: 1700,
            timeSlots: getTimeSlots({
                date: eventDay.date,
                startTime: 800,
                endTime: 1700,
                duration: 30,
            }),
        }));

        setOpenHoursByDay(eventDaysWithHours);
    }, [event, getTimeSlots]);

    // -----------------------------
    // Render
    // -----------------------------

    if (!openHoursByDay) {
        return <LoadingArea />;
    }

    return (
        <div className="my-event-availability">
            <DividerLightGreyStyled className="mb-4 mt-0" />
            <H4 className="mb-3">Set your availability during this event.</H4>
            <BodyMedium className="mb-4">
                Add in your availability to let people know when they can schedule appointments with you.
            </BodyMedium>
            <DividerLightGreyStyled className="mb-1.875rem mt-0" />

            <TimeSlotsWrapperOuter>
                {openHoursByDay.map((day, dayIndex) => {
                    const getTimeFormatted = time => {
                        const hourOf24 = time / 100;
                        const hourOf12 = hourOf24 > 12 ? hourOf24 - 12 : hourOf24;
                        const minutes = (time % hourOf24).toString().padStart(2, '0');
                        const amPM = hourOf24 >= 12 ? 'pm' : 'am';
                        return `${hourOf12}:${minutes} ${amPM}`;
                    };

                    return (
                        <React.Fragment key={day.date}>
                            <TimeSlotsWrapperInner
                                key={day.date}
                                className={classnames({
                                    border: dayIndex % 2 === 0 && dayIndex < openHoursByDay.length - 1,
                                })}
                            >
                                <TimeSlotsPanel
                                    header={`Appointment Slots on ${formatDateInEastern(day.date, 'EEEE, MMMM d')}`}
                                    subheader={`Event runs from ${getTimeFormatted(day.startTime)} - ${getTimeFormatted(
                                        day.endTime
                                    )}`}
                                    timeSlots={day.timeSlots}
                                    onTimeSlotClick={toggleAvailability}
                                />
                            </TimeSlotsWrapperInner>

                            {dayIndex % 2 === 1 && (
                                <DividerLightGreyStyled className="mt-0 mb-4" style={{ width: 'calc(100% - 38px)' }} />
                            )}
                        </React.Fragment>
                    );
                })}
            </TimeSlotsWrapperOuter>

            <DividerLightGreyStyled className="mb-2rem mt-0" />

            <ButtonRedesign>Save Availability</ButtonRedesign>
        </div>
    );
};

export default MyEventAvailability;
